import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, BrowserRouter as Router } from 'react-router-dom';
import Papa from 'papaparse';
import CodingForm from './CodingForm';
import FooterNav from './FooterNav';
import TikTokEmbed from './TikTokEmbed';
import TikTokMetadata from './MetaData';
import { Modal, Button } from 'react-bootstrap';
import Split from 'react-split';
import { ArrowLeft } from 'react-bootstrap-icons';
import ReactDOM from 'react-dom';
import { API_BASE_URL } from './api';
import axios from 'axios';
import CodebookViewer from './CodebookViewer';

export default TikTokCodingTool;

function TikTokCodingTool() {
  const location = useLocation();
  const navigate = useNavigate();
  const project = location.state?.project;
  const initialCoder = location.state?.coderName || '';

  // Redirect to homepage if project data is missing (e.g., on page reload)
  useEffect(() => {
    if (!project) {
      navigate('/', { replace: true });
    }
  }, [project, navigate]);

  const [previousCoderName, setPreviousCoderName] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showProjectEditor, setShowProjectEditor] = useState(false);
  const [showCodebookModal, setShowCodebookModal] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [videos, setVideos] = useState([]);
  const [totalVideos, setTotalVideos] = useState(0);
  const [projectData, setProjectData] = useState(project);
  const [categories, setCategories] = useState({});
  const [codebookList, setCodebookList] = useState([]);
  const [coderName, setCoderName] = useState(initialCoder);
  const [responses, setResponses] = useState(() => {
    const stored = localStorage.getItem('responses');
    return stored ? JSON.parse(stored) : {};
  });
  const [originalResponse, setOriginalResponse] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [filter, setFilter] = useState('all');
  const [filterHasNoResults, setFilterHasNoResults] = useState(false);
  // Flag to prevent autosave immediately after submit
  const justSubmittedRef = useRef(false);
  
  // Function to navigate to a different video
  const goToVideo = (newIndex) => {
    if (!coderName.trim()) {
      setShowWarningModal(true);
      return;
    }
    // Only save if there are actual changes
    const updatedResponses = {
      ...responses,
      [currentVideo?.id]: {
        ...(responses[currentVideo?.id] || {}),
        [coderName]: currentResponse
      }
    };
    saveResponses(updatedResponses);
    setCurrentIndex(newIndex);
  };
  
  // Reset to first video when filter changes
  useEffect(() => {
    setCurrentIndex(0);
    setFilterHasNoResults(false);
  }, [filter]);

    useEffect(() => {
    axios.get(`${API_BASE_URL}/api/video-at-index`, {
        params: {
        project: project.slug,
        coder: coderName,
        index: currentIndex,
        filter: filter
        }
    })
        .then(res => {
        console.log("Video fetched:", res.data);
        console.log("Filter:", filter);
        console.log("Has filtered_videos:", res.data.filtered_videos !== undefined);
        
        // Load existing response data including status from backend (for non-filtered responses)
        if (res.data && res.data.response && res.data.id && res.data.filtered_videos === undefined) {
          const videoId = res.data.id;
          const responseData = res.data.response;
          setResponses((prev) => ({
            ...prev,
            [videoId]: {
              ...prev[videoId],
              [coderName]: {
                ...prev[videoId]?.[coderName],
                ...responseData,
                status: responseData.status || "draft",
                excluded: responseData.excluded || false
              }
            }
          }));
        }
        
        // Handle filtered response
        if (res.data.filtered_videos !== undefined) {
            // When filter is applied, we get a list of filtered videos
            const filteredVideos = res.data.filtered_videos;
            setTotalVideos(filteredVideos.length);
            
            // If there are filtered videos, get the current one
            if (filteredVideos.length > 0 && currentIndex < filteredVideos.length) {
                const currentFilteredVideo = filteredVideos[currentIndex];
                console.log("Current filtered video:", currentFilteredVideo);
                console.log("Video data:", currentFilteredVideo.video);
                console.log("Video metadata:", currentFilteredVideo.video?.metadata);
                setVideos((prev) => {
                    const updated = [...prev];
                    updated[currentIndex] = currentFilteredVideo.video;
                    return updated;
                });
                setFilterHasNoResults(false);
            } else if (filteredVideos.length === 0) {
                // No videos match the filter
                setVideos([]);
                setFilterHasNoResults(true);
            }
        } else {
            // Normal response for 'all' filter
            setVideos((prev) => {
                const updated = [...prev];
                updated[currentIndex] = res.data;
                return updated;
            });
            if (res.data.total) {
                setTotalVideos(res.data.total);
            }
            setFilterHasNoResults(false);
        }
        })
        .catch(err => console.error("Video fetch error:", err));
    }, [currentIndex, project, coderName, filter]);

  // Fetch latest project info (including codebook) so coding page reflects homepage edits
  useEffect(() => {
    if (!project?.slug) return;
    axios.get(`${API_BASE_URL}/api/project-info`, { params: { project: project.slug } })
      .then(res => {
        const info = res.data || {};
        setProjectData(info);
        const raw = info.codebook || [];
        setCodebookList(raw);
        const formatted = {};
        raw.forEach((c) => {
          const catName = c.category || '';
          const incoming = (c.tags || [])
            .map((t) => (typeof t === 'string' ? t : (t && t.tag)))
            .filter((t) => typeof t === 'string' && t.length > 0);
          formatted[catName] = (formatted[catName] || []).concat(incoming);
        });
        setCategories(formatted);
      })
      .catch(err => console.error('Failed to load project info/codebook:', err));
  }, [project?.slug]);

useEffect(() => {
  if (document.querySelector('script[src^="https://www.tiktok.com/embed.js"]')) return;
  const s = document.createElement('script');
  s.src = 'https://www.tiktok.com/embed.js';
  s.async = true;
  document.body.appendChild(s);
}, []); // load once

  // Prevent body scrolling on coding page
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
    };
  }, []);

  // Track original response when video or coder changes
  useEffect(() => {
    if (videos[currentIndex] && coderName) {
      const currentVideo = videos[currentIndex];
      const currentResponse = responses[currentVideo.id]?.[coderName] || {};
      setOriginalResponse(currentResponse);
      setHasUnsavedChanges(false);
    }
  }, [videos, currentIndex, coderName, responses]);

  if (!videos[currentIndex]) {
    if (filterHasNoResults) {
      return (
        <div className="d-flex flex-column vh-100">
          <div className="container-fluid flex-grow-1 d-flex flex-column h-100">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center border-bottom py-2 px-3">
              <Button variant="outline-secondary" onClick={() => navigate('/')}> <ArrowLeft className="me-2" />Back</Button>
              <div className="text-center">
                <h4 className="mb-1 fw-bold">{project?.name || 'Project'}</h4>
                <div className="fw-semibold" style={{ fontSize: '1rem' }}>Coder: {coderName}</div>
              </div>
              <Button variant="outline-primary" onClick={() => setShowCodebookModal(true)}>Show Codebook</Button>
            </div>

            {/* No results message */}
            <div className="flex-grow-1 d-flex align-items-center justify-content-center">
              <div className="alert alert-info text-center mb-3 mx-3">
                <i className="bi bi-info-circle me-2"></i>
                <strong>No {filter === 'uncoded' ? 'uncoded' : filter === 'saved' ? 'draft' : filter} videos found.</strong>
                <br />
                <small>Try selecting a different filter or check back later.</small>
              </div>
            </div>

            {/* Footer */}
            <div className="row justify-content-between align-items-center border-top p-3">
              <FooterNav
                coderName={coderName}
                currentIndex={currentIndex}
                goToVideo={goToVideo}
                projectSlug={project.slug}
                videoId={null}
                currentResponse={{}}
                totalVideos={totalVideos}
                setTotalVideos={setTotalVideos}
                filter={filter}
                setFilter={setFilter}
                filterHasNoResults={filterHasNoResults}
              />
            </div>
          </div>
        </div>
      );
    }
    return <div className='p-4'>Loading video...</div>;
  }

  const currentVideo = videos[currentIndex];
  const currentResponse = responses[currentVideo.id]?.[coderName] || {};

  // Helper function to compare responses for changes
  const responsesAreEqual = (response1, response2) => {
    if (!response1 && !response2) return true;
    if (!response1 || !response2) return false;
    
    const keys1 = Object.keys(response1);
    const keys2 = Object.keys(response2);
    
    if (keys1.length !== keys2.length) return false;
    
    for (let key of keys1) {
      if (key === 'notes') {
        if (response1[key] !== response2[key]) return false;
      } else if (Array.isArray(response1[key]) && Array.isArray(response2[key])) {
        if (response1[key].length !== response2[key].length) return false;
        if (!response1[key].every((item, index) => item === response2[key][index])) return false;
      } else if (response1[key] !== response2[key]) {
        return false;
      }
    }
    return true;
  };

  const saveResponses = (updatedResponses) => {
    // Check both incoming payload status and current state to prevent autosave after submit
    const responseStatus = updatedResponses[currentVideo.id]?.[coderName]?.status;
    const currentStatus = responses[currentVideo.id]?.[coderName]?.status;
    
    // Prevent autosave if:
    // 1. Incoming payload has submitted status
    // 2. Current state is submitted
    // 3. We just submitted (flag set)
    if (responseStatus === "submitted" || currentStatus === "submitted" || justSubmittedRef.current) {
      console.log("Skipping autosave - record is already submitted or just submitted");
      return;
    }
    
    setResponses(updatedResponses);
    
    const newResponse = updatedResponses[currentVideo.id]?.[coderName];
    const hasChanges = !responsesAreEqual(originalResponse, newResponse);
    
    if (hasChanges) {
      setHasUnsavedChanges(true);
      axios.post(`${API_BASE_URL}/api/save-progress`, {
        project: project.slug,
        coder: coderName,
        video_id: currentVideo.id,
        response: newResponse
      }).then((res) => {
        setHasUnsavedChanges(false);
        setOriginalResponse(newResponse);
        // Update status from backend response if provided
        if (res.data && res.data.status) {
          setResponses((prev) => ({
            ...prev,
            [currentVideo.id]: {
              ...prev[currentVideo.id],
              [coderName]: {
                ...prev[currentVideo.id]?.[coderName],
                status: res.data.status
              }
            }
          }));
        }
      }).catch(console.error);
    }
  };

  const addCategory = (name) => {
    if (!categories[name]) {
      setCategories({ ...categories, [name]: [] });
    }
  };

  const addResponseOption = (category, option) => {
    if (categories[category] && !categories[category].includes(option)) {
      setCategories({
        ...categories,
        [category]: [...categories[category], option],
      });
    }
  };

  const updateResponse = (category, values) => {
    if (!coderName.trim()) {
      setShowWarningModal(true);
      return;
    }
    const updated = {
      ...responses,
      [currentVideo.id]: {
        ...(responses[currentVideo.id] || {}),
        [coderName]: {
          ...(responses[currentVideo.id]?.[coderName] || {}),
          [category]: values,
        },
      },
    };
    saveResponses(updated);
  };

  const handleNoteChange = (e) => {
    if (!coderName.trim()) {
      setShowWarningModal(true);
      return;
    }
    const updated = {
      ...responses,
      [currentVideo.id]: {
        ...(responses[currentVideo.id] || {}),
        [coderName]: {
          ...(responses[currentVideo.id]?.[coderName] || {}),
          notes: e.target.value,
        },
      },
    };
    saveResponses(updated);
  };


  const handleSaveProject = (updatedProject) => {
    setProjectData(updatedProject);
    const updated = {};
    updatedProject.codebook.forEach((c) => {
      updated[c.category] = c.tags.map((t) => t.tag);
    });
    setCategories(updated);
  };

  // Callback to update status after successful submit
  const handleSubmitSuccess = (submitResponse) => {
    // Set flag to prevent autosave immediately after submit
    justSubmittedRef.current = true;
    
    // Update state with submitted status (use response status if available, otherwise default to "submitted")
    const submittedStatus = submitResponse?.data?.status || "submitted";
    setResponses((prev) => ({
      ...prev,
      [currentVideo.id]: {
        ...prev[currentVideo.id],
        [coderName]: {
          ...prev[currentVideo.id]?.[coderName],
          status: submittedStatus
        }
      }
    }));
    
    // Update original response to prevent autosave from detecting changes
    setOriginalResponse((prev) => ({
      ...prev,
      status: submittedStatus
    }));
    
    // Clear the flag after a short delay to allow any pending autosaves to complete
    setTimeout(() => {
      justSubmittedRef.current = false;
    }, 2000);
  };

  return (
    <div className="d-flex flex-column" style={{ overflow: 'hidden', height: '100vh', width: '100vw', margin: 0, padding: 0 }}>
      <div className="d-flex flex-column" style={{ minHeight: 0, overflow: 'hidden', flex: '1 1 0', height: 0, margin: 0, padding: 0 }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center border-bottom py-2 px-3" style={{ flexShrink: 0, flexGrow: 0 }}>
          <Button variant="outline-secondary" onClick={() => navigate('/')}> <ArrowLeft className="me-2" />Back</Button>
          <div className="text-center">
            <h4 className="mb-1 fw-bold">{project?.name || 'Project'}</h4>
            <div className="fw-semibold" style={{ fontSize: '1rem' }}>Coder: {coderName}</div>
          </div>
          <Button variant="outline-primary" onClick={() => setShowCodebookModal(true)}>Show Codebook</Button>
        </div>

        {/* Show message when filter has no results */}
        {filterHasNoResults && (
          <div className="alert alert-info text-center mb-3 mx-3" style={{ flexShrink: 0, flexGrow: 0 }}>
            <i className="bi bi-info-circle me-2"></i>
            <strong>No {filter === 'uncoded' ? 'uncoded' : filter === 'saved' ? 'draft' : filter} videos found.</strong>
            <br />
            <small>Try selecting a different filter or check back later.</small>
          </div>
        )}

        <Split
          className="d-flex"
          style={{ flex: '1 1 0', minHeight: 0, overflow: 'hidden' }}
          sizes={[66, 34]}
          minSize={200}
          gutterSize={12}
          direction="horizontal"
          gutter={() => {
            const gutter = document.createElement('div');
            gutter.className = 'custom-gutter d-flex align-items-center me-2';

            const icon = document.createElement('div');
            icon.className = 'gutter-icon h2 me-2 mb-0';
            icon.textContent = '⋮';
            gutter.appendChild(icon);

            return gutter;
          }}
        >
          {/* Left column */}
          <div className="d-flex flex-column w-100" style={{ height: '100%', overflow: 'hidden' }}>
            <div className="row flex-grow-1" style={{ height: '100%', minHeight: 0 }}>
              <div className="col-md-6 d-flex flex-column h-100">
                <div className="p-3" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                  <TikTokEmbed video={currentVideo} />
                </div>
              </div>
              <div className="col-md-6 d-flex flex-column h-100 p-3" style={{ minHeight: 0 }}>
                <TikTokMetadata metadata={currentVideo.metadata} />
                {hasUnsavedChanges && (
                  <div className="mt-2">
                    <small className="text-warning">
                      <i className="bi bi-exclamation-triangle-fill me-1"></i>
                      Unsaved changes
                    </small>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="border-start p-2 d-flex flex-column" style={{ height: '100%', overflow: 'hidden' }}>
            <div style={{ overflowY: 'auto', height: '100%', overflowX: 'hidden' }}>
              <CodingForm
              key={`${currentVideo.id}-${coderName}`}
              categories={categories}
              orderedCategories={codebookList}
              currentResponse={currentResponse}
              coderName={coderName}
              setShowWarning={setShowWarningModal}
              addResponseOption={addResponseOption}
              addCategory={addCategory}
              updateResponse={updateResponse}
              handleNoteChange={handleNoteChange}
              projectSlug={project.slug}
              videoId={currentVideo.id}
              project={projectData}
              onSaveProject={handleSaveProject}
              onSubmitSuccess={handleSubmitSuccess}
            />
            </div>
          </div>
        </Split>

        <div className="d-flex justify-content-between align-items-center border-top p-3" style={{ flexShrink: 0, flexGrow: 0, margin: 0 }}>
          <FooterNav
            coderName={coderName}
            currentIndex={currentIndex}
            goToVideo={goToVideo}
            projectSlug={project.slug}
            videoId={currentVideo.id}
            currentResponse={currentResponse}
            totalVideos={totalVideos}
            setTotalVideos={setTotalVideos}
            filter={filter}
            setFilter={setFilter}
            filterHasNoResults={filterHasNoResults}
          />
        </div>
      </div>

      <CodebookViewer
        show={showCodebookModal}
        onClose={() => setShowCodebookModal(false)}
        project={projectData}
        onSave={handleSaveProject}
      />
      <Modal show={showWarningModal} onHide={() => setShowWarningModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Coder Name Required</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Please enter your coder name before tagging or making notes.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowWarningModal(false)}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
